/**
 * Production-ready logging service with structured logging and correlation IDs.
 * 
 * This service provides comprehensive logging capabilities including:
 * - Structured JSON logging with consistent field names
 * - Correlation ID tracking for distributed tracing
 * - Performance metrics and timing
 * - Error context and stack traces
 * - User and business context logging
 * 
 * @example
 * ```typescript
 * const logger = Logger.getInstance();
 * logger.info('User action completed', {
 *   correlationId: 'req_123',
 *   userId: 'user_456',
 *   operation: 'loadPage',
 *   duration: 150
 * });
 * ```
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Log levels in order of severity
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

/**
 * Structured log context interface for consistent logging
 */
export interface LogContext {
  /** Unique identifier for request/operation tracing */
  correlationId: string;
  /** User identifier for user-specific operations */
  userId?: string;
  /** Document identifier for document operations */
  documentId?: string;
  /** Page number for page-specific operations */
  pageNum?: number;
  /** Operation name for categorizing logs */
  operation: string;
  /** Operation duration in milliseconds */
  duration?: number;
  /** Error message for error logs */
  error?: string;
  /** Error stack trace for debugging */
  stack?: string;
  /** Additional metadata */
  metadata?: Record<string, any>;
  /** Timestamp of the log entry */
  timestamp?: string;
}

/**
 * Performance metrics interface for monitoring
 */
export interface PerformanceMetrics {
  /** Operation start time */
  startTime: number;
  /** Operation end time */
  endTime?: number;
  /** Operation duration in milliseconds */
  duration?: number;
  /** Success/failure status */
  success: boolean;
  /** Error details if operation failed */
  error?: string;
}

/**
 * Production-ready logger with structured logging and performance monitoring
 */
export class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;
  private correlationIdKey = 'correlationId';

  private constructor() {
    // Set log level based on environment
    this.logLevel = this.getLogLevelFromEnvironment();
  }

  /**
   * Get singleton logger instance
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Generate a new correlation ID for request tracing
   */
  public generateCorrelationId(): string {
    return `req_${uuidv4().substring(0, 8)}`;
  }

  /**
   * Set correlation ID in session storage for request tracking
   */
  public setCorrelationId(correlationId: string): void {
    sessionStorage.setItem(this.correlationIdKey, correlationId);
  }

  /**
   * Get current correlation ID from session storage
   */
  public getCorrelationId(): string {
    return sessionStorage.getItem(this.correlationIdKey) || this.generateCorrelationId();
  }

  /**
   * Create performance timer for operation monitoring
   */
  public startTimer(operation: string): PerformanceMetrics {
    return {
      startTime: Date.now(),
      success: false
    };
  }

  /**
   * Complete performance timer and log metrics
   */
  public endTimer(
    timer: PerformanceMetrics, 
    context: Omit<LogContext, 'duration' | 'timestamp'>
  ): void {
    timer.endTime = Date.now();
    timer.duration = timer.endTime - timer.startTime;
    timer.success = true;

    this.info('Operation completed', {
      ...context,
      duration: timer.duration,
      metadata: {
        performanceMetrics: timer
      }
    });
  }

  /**
   * Log debug message with context
   */
  public debug(message: string, context: LogContext): void {
    if (this.logLevel <= LogLevel.DEBUG) {
      this.log('DEBUG', message, context);
    }
  }

  /**
   * Log info message with context
   */
  public info(message: string, context: LogContext): void {
    if (this.logLevel <= LogLevel.INFO) {
      this.log('INFO', message, context);
    }
  }

  /**
   * Log warning message with context
   */
  public warn(message: string, context: LogContext): void {
    if (this.logLevel <= LogLevel.WARN) {
      this.log('WARN', message, context);
    }
  }

  /**
   * Log error message with context and stack trace
   */
  public error(message: string, context: LogContext): void {
    if (this.logLevel <= LogLevel.ERROR) {
      this.log('ERROR', message, context);
    }
  }

  /**
   * Log API call with timing and response details
   */
  public logApiCall(
    method: string,
    url: string,
    status: number,
    duration: number,
    context: Omit<LogContext, 'operation' | 'duration'>
  ): void {
    const logContext: LogContext = {
      ...context,
      operation: 'apiCall',
      duration,
      metadata: {
        method,
        url,
        status,
        success: status >= 200 && status < 300
      }
    };

    if (status >= 400) {
      this.error(`API call failed: ${method} ${url}`, logContext);
    } else {
      this.info(`API call completed: ${method} ${url}`, logContext);
    }
  }

  /**
   * Log user interaction for analytics
   */
  public logUserInteraction(
    action: string,
    element: string,
    context: Omit<LogContext, 'operation'>
  ): void {
    this.info(`User interaction: ${action}`, {
      ...context,
      operation: 'userInteraction',
      metadata: {
        action,
        element,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log performance metrics for monitoring
   */
  public logPerformance(
    metric: string,
    value: number,
    context: Omit<LogContext, 'operation'>
  ): void {
    this.info(`Performance metric: ${metric}`, {
      ...context,
      operation: 'performanceMetric',
      metadata: {
        metric,
        value,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Internal logging method with structured output
   */
  private log(level: string, message: string, context: LogContext): void {
    const logEntry = {
      level,
      message,
      timestamp: context.timestamp || new Date().toISOString(),
      correlationId: context.correlationId,
      userId: context.userId,
      documentId: context.documentId,
      pageNum: context.pageNum,
      operation: context.operation,
      duration: context.duration,
      error: context.error,
      stack: context.stack,
      metadata: context.metadata,
      // Browser context
      userAgent: navigator.userAgent,
      url: window.location.href,
      // Environment context
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0'
    };

    // Remove undefined fields for cleaner logs
    Object.keys(logEntry).forEach(key => {
      if (logEntry[key as keyof typeof logEntry] === undefined) {
        delete logEntry[key as keyof typeof logEntry];
      }
    });

    // Output to console with appropriate method
    switch (level) {
      case 'DEBUG':
        console.debug(JSON.stringify(logEntry));
        break;
      case 'INFO':
        console.info(JSON.stringify(logEntry));
        break;
      case 'WARN':
        console.warn(JSON.stringify(logEntry));
        break;
      case 'ERROR':
        console.error(JSON.stringify(logEntry));
        break;
      default:
        console.log(JSON.stringify(logEntry));
    }

    // In production, also send to logging service
    if (process.env.NODE_ENV === 'production') {
      this.sendToLoggingService(logEntry);
    }
  }

  /**
   * Send logs to external logging service in production
   */
  private async sendToLoggingService(logEntry: any): Promise<void> {
    try {
      // In a real implementation, this would send to services like:
      // - AWS CloudWatch Logs
      // - Datadog
      // - New Relic
      // - Custom logging endpoint
      
      const loggingEndpoint = process.env.REACT_APP_LOGGING_ENDPOINT;
      if (loggingEndpoint) {
        await fetch(loggingEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logEntry)
        });
      }
    } catch (error) {
      // Fail silently to avoid logging loops
      console.error('Failed to send log to external service:', error);
    }
  }

  /**
   * Get log level from environment configuration
   */
  private getLogLevelFromEnvironment(): LogLevel {
    const envLogLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
    
    switch (envLogLevel) {
      case 'DEBUG':
        return LogLevel.DEBUG;
      case 'INFO':
        return LogLevel.INFO;
      case 'WARN':
        return LogLevel.WARN;
      case 'ERROR':
        return LogLevel.ERROR;
      default:
        // Default to INFO in production, DEBUG in development
        return process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG;
    }
  }
}

// Export singleton instance for convenience
export const logger = Logger.getInstance();
export default logger;